import { useEffect } from "react";
import { routeSeo, resolveRouteKey } from "@shared/seo-routes";

interface SEOProps {
  title: string;
  description: string;
  path: string;
  lang?: "nl" | "en";
  ogTitle?: string;
  ogDescription?: string;
  /** @deprecated meta keywords worden bewust niet meer gerenderd (over-optimalisatiesignaal). */
  keywords?: string;
}

const BASE_URL = "https://wrkt.nl";

function setMetaTag(selector: string, attribute: string, value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attribute, value);
}

function removeMetaTag(selector: string) {
  const el = document.querySelector(selector);
  if (el) el.remove();
}

/**
 * Zet per-route SEO-meta. Voor statische routes komt title/description uit de
 * gedeelde config (shared/seo-routes.ts) — dezelfde bron als de SSR — zodat de
 * title na hydration NIET verandert. De props gelden alleen als fallback voor
 * dynamische routes (artikelen/cases) en zijn daar via gedeelde helpers
 * eveneens gelijk aan de SSR-waarden.
 */
export function useSEO({ title, description, path, lang = "nl", ogTitle, ogDescription }: SEOProps) {
  useEffect(() => {
    const safePath = path || "/";
    const shared = routeSeo[resolveRouteKey(safePath, lang)];
    const finalTitle = shared?.title ?? title;
    const finalDescription = shared?.description ?? description;

    document.title = finalTitle;

    const nlUrl = `${BASE_URL}${safePath}`;
    const enUrl = safePath === "/" ? `${BASE_URL}/en` : `${BASE_URL}/en${safePath}`;
    const currentUrl = lang === "en" ? enUrl : nlUrl;

    setMetaTag('meta[name="description"]', "content", finalDescription);

    // Meta keywords bewust verwijderen (ook als de SSR-template ze nog bevat).
    removeMetaTag('meta[name="keywords"]');

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", currentUrl);

    setMetaTag('meta[property="og:title"]', "content", ogTitle || finalTitle);
    setMetaTag('meta[property="og:description"]', "content", ogDescription || finalDescription);
    setMetaTag('meta[property="og:url"]', "content", currentUrl);
    setMetaTag('meta[property="og:locale"]', "content", lang === "en" ? "en_US" : "nl_NL");

    setMetaTag('meta[name="twitter:title"]', "content", ogTitle || finalTitle);
    setMetaTag('meta[name="twitter:description"]', "content", ogDescription || finalDescription);

    const hreflangNl = document.querySelector('link[hreflang="nl"]');
    const hreflangEn = document.querySelector('link[hreflang="en"]');
    const hreflangDefault = document.querySelector('link[hreflang="x-default"]');
    if (hreflangNl) hreflangNl.setAttribute("href", nlUrl);
    if (hreflangEn) hreflangEn.setAttribute("href", enUrl);
    if (hreflangDefault) hreflangDefault.setAttribute("href", nlUrl);
  }, [title, description, path, lang, ogTitle, ogDescription]);
}
